import * as R from 'ramda';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import getPayload from 'selectors/getPayload';
import { selectors as customerSelectors } from './shared/ducks/customer.duck';
import Account from './shared/components/Account';
import {
  ListNavItemPropDef,
  ListNavItemChildPropDef,
} from 'components/general/ListNav/ListNav';
import getUserAbility from 'selectors/getUserAbility';

// Side-bar navigation items
const getOrgNavItems = (ability): (ListNavItemPropDef|any[]) => (
  R.pipe(
    // The base nav items, everyone gets these.
    R.append<ListNavItemPropDef>({
      id: 'preferences',
      title: 'Preferences',
      children: [
        {
          id: 'user_profile',
          title: 'Profile',
          to: {
            type: 'PREFERENCES',
          },
          exact: true,
        },
        {
          id: 'user_interests',
          title: 'Interests',
          to: {
            type: 'PREFERENCES_INTERESTS',
          },
        },
        {
          id: 'user_emails',
          title: 'Emails',
          to: {
            type: 'PREFERENCES_EMAILS',
          },
        },
        {
          id: 'log-out',
          title: 'Log out',
          to: {
            type: 'LOGOUT',
          },
        },
      ],
    }),
    // The organisation menu
    R.when(
      R.always(
        ability.can('manage', 'Organisation')
        || ability.can('read', 'Customer'),
      ),
      R.append({
        id: 'organisation',
        title: 'Organisation',
        children: R.pipe(
          R.when(
            R.always(
              ability.can('manage', 'Organisation'),
            ),
            R.append<ListNavItemChildPropDef>(
              {
                id: 'organisation_profile',
                title: 'Profile',
                to: {
                  type: 'ORGANISATION',
                },
                exact: true,
              },
            ),
          ),
          R.when(
            R.always(
              ability.can('manage', 'Organisation'),
            ),
            R.append<ListNavItemChildPropDef>(
              {
                id: 'organisation_users',
                title: 'Users',
                to: {
                  type: 'ORGANISATION_USERS',
                },
              },
            ),
          ),
          R.when(
            R.always(
              ability.can('manage', 'Organisation'),
            ),
            R.append<ListNavItemChildPropDef>(
              {
                id: 'organisation_invitations',
                title: 'Invitations',
                to: {
                  type: 'ORGANISATION_INVITATIONS',
                },
              },
            ),
          ),
        )([]),
      }),
    ),
    R.when(
      R.always(
        ability.can('manage', 'Subscription') || ability.can('read', 'Customer'),
      ),
      R.append({
        id: 'admin',
        title: 'Admin',
        children: R.pipe(
          R.when(
            R.always(
              ability.can('manage', 'Customer'),
            ),
            R.append<ListNavItemChildPropDef>(
              {
                id: 'organisation_customers',
                title: 'Customers',
                to: {
                  type: 'ORGANISATION_CUSTOMERS',
                },
              },
            ),
          ),
          R.when(
            R.always(
              ability.can('manage', 'Organisation'),
            ),
            R.append<ListNavItemChildPropDef>(
              {
                id: 'organisation_subscriptions',
                title: 'Subscriptions',
                to: {
                  type: 'ADMIN_SUBSCRIPTIONS',
                },
              },
            ),
          ),

        )([]),
      }),
    ),
  )([])
);

// Side-bar navigation items for customers
const getCustomerNavItems = (ability, data): ListNavItemPropDef|any[] => ([
  {
    id: 'customer',
    title: 'Customer',
    children: R.pipe(
      R.when(
        R.always(
          ability.can('manage', 'Subscription'),
        ),
        R.append<ListNavItemChildPropDef>({
          id: 'customer_subscription',
          title: 'Subscription',
          to: {
            type: 'ORGANISATION_CUSTOMERS_SUBSCRIPTION',
            payload: {
              customerId: R.prop('id', data),
            },
          },
          exact: true,
        }),
      ),
    )(
      [
        {
          id: 'customer_profile',
          title: 'Profile',
          to: {
            type: 'ORGANISATION_CUSTOMERS_SINGLE',
            payload: {
              customerId: R.prop('id', data),
            },
          },
          exact: true,
        },
        {
          id: 'customer_users',
          title: 'Users',
          to: {
            type: 'ORGANISATION_CUSTOMERS_SINGLE_USERS',
            payload: {
              customerId: R.prop('id', data),
            },
          },
        },
        {
          id: 'customer_invitations',
          title: 'Invitations',
          to: {
            type: 'ORGANISATION_CUSTOMERS_SINGLE_INVITATIONS',
            payload: {
              customerId: R.prop('id', data),
            },
          },
        },
      ],
    ),
  },
]);

const getCustomer = createSelector(
  getPayload,
  customerSelectors.data,
  customerSelectors.loading,
  (payload, data, loading) => {
    if (R.has('customerId', payload)) {
      return {
        data,
        loading,
      };
    }
    return {};
  },
);

const getNavItems = createSelector(
  getCustomer,
  getUserAbility,
  (customer, ability) => {
    if (ability.can('manage', 'Customer') && customer && customer.data) {
      return getCustomerNavItems(ability, customer.data);
    }

    return getOrgNavItems(ability);
  },
);

const AccountContainer = connect(R.applySpec({
  customer: getCustomer,
  navItems: getNavItems,
}))(Account);

export default AccountContainer;
